import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { organizationsApi } from '../../api/client';
import { useAuthStore } from '../../stores/auth.store';
import { useCreateUser } from '../../hooks/useUsers';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { AttendanceHistoryPanel } from '../../components/dashboard/AttendanceHistoryPanel';
import { formatDate } from '../../lib/utils';
import { roleLabel } from '../../lib/roles';

function AddEmployeeForm({ onDone }: { onDone: () => void }) {
  const [form, setForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'member',
  });
  const createUser = useCreateUser();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createUser.mutate(form, { onSuccess: onDone });
  };

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 border rounded-md bg-gray-50">
      <Input
        placeholder="Email"
        type="email"
        required
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
      />
      <Input
        placeholder="Temporary password"
        type="password"
        required
        value={form.password}
        onChange={(e) => setForm({ ...form, password: e.target.value })}
      />
      <Input
        placeholder="First name"
        required
        value={form.firstName}
        onChange={(e) => setForm({ ...form, firstName: e.target.value })}
      />
      <Input
        placeholder="Last name"
        required
        value={form.lastName}
        onChange={(e) => setForm({ ...form, lastName: e.target.value })}
      />
      <select
        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
        value={form.role}
        onChange={(e) => setForm({ ...form, role: e.target.value })}
      >
        <option value="member">Employee</option>
        <option value="manager">Manager</option>
        <option value="admin">Admin</option>
      </select>
      <div className="flex gap-2">
        <Button type="submit" disabled={createUser.isPending}>
          {createUser.isPending ? 'Creating...' : 'Create'}
        </Button>
        <Button type="button" variant="outline" onClick={onDone}>
          Cancel
        </Button>
      </div>
      {createUser.isError && (
        <p className="col-span-2 text-sm text-red-600">
          {(createUser.error as any)?.response?.data?.message || 'Failed to create account'}
        </p>
      )}
    </form>
  );
}

export default function TeamPage() {
  const user = useAuthStore((state) => state.user);
  const isManager = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'manager';
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const [showAddForm, setShowAddForm] = useState(false);
  const [viewingAttendanceFor, setViewingAttendanceFor] = useState<{ id: string; name: string } | null>(
    null
  );

  const { data: organization, isLoading: loadingOrg } = useQuery({
    queryKey: ['organization'],
    queryFn: async () => {
      const response = await organizationsApi.getCurrent();
      return response.data;
    },
  });

  const { data: members, isLoading: loadingMembers } = useQuery({
    queryKey: ['members'],
    queryFn: async () => {
      const response = await organizationsApi.getMembers();
      return response.data;
    },
    enabled: isManager,
  });

  const getRoleBadge = (role: string) => {
    const colors: Record<string, string> = {
      owner: 'bg-purple-100 text-purple-700',
      admin: 'bg-blue-100 text-blue-700',
      manager: 'bg-green-100 text-green-700',
      member: 'bg-gray-100 text-gray-700',
    };
    return colors[role] || colors.member;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Team</h1>
        <p className="text-gray-600">Manage your organization</p>
      </div>

      {/* Organization Info */}
      <Card>
        <CardHeader>
          <CardTitle>Organization</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingOrg ? (
            <p className="text-gray-500">Loading...</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium">{organization?.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Members</p>
                <p className="font-medium">{organization?.userCount || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Devices</p>
                <p className="font-medium">{organization?.deviceCount || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Active Devices</p>
                <p className="font-medium">{organization?.activeDeviceCount || 0}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Team Members */}
      {isManager && (
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle>Team Members</CardTitle>
            {isAdmin && !showAddForm && (
              <Button size="sm" onClick={() => setShowAddForm(true)}>
                Add Employee
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {isAdmin && showAddForm && (
              <div className="mb-4">
                <AddEmployeeForm onDone={() => setShowAddForm(false)} />
              </div>
            )}
            {loadingMembers ? (
              <p className="text-gray-500">Loading...</p>
            ) : members?.length === 0 ? (
              <p className="text-gray-500">No team members</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-4">Name</th>
                      <th className="text-left py-2 px-4">Email</th>
                      <th className="text-left py-2 px-4">Role</th>
                      <th className="text-left py-2 px-4">Status</th>
                      <th className="text-left py-2 px-4">Joined</th>
                      <th className="text-left py-2 px-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {members?.map((member: any) => (
                      <tr key={member.id} className="border-b last:border-0">
                        <td className="py-3 px-4">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-sm font-medium mr-3">
                              {member.firstName?.[0]}
                              {member.lastName?.[0]}
                            </div>
                            <span className="font-medium">
                              {member.firstName} {member.lastName}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-500">{member.email}</td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${getRoleBadge(
                              member.role
                            )}`}
                          >
                            {roleLabel(member.role)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              member.isActive
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {member.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-500">{formatDate(member.createdAt)}</td>
                        <td className="py-3 px-4">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              setViewingAttendanceFor({
                                id: member.id,
                                name: `${member.firstName} ${member.lastName}`,
                              })
                            }
                          >
                            View Attendance
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {viewingAttendanceFor && (
              <div className="mt-4">
                <AttendanceHistoryPanel
                  userId={viewingAttendanceFor.id}
                  userName={viewingAttendanceFor.name}
                  onClose={() => setViewingAttendanceFor(null)}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!isManager && (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-gray-500">
              You need manager permissions or higher to view team members
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
